import React, { useState } from 'react';
import { getCalendarDays, isSameDay, isPastDate } from '../../utils/dateUtils';

interface RangeCalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  onSelectRange: (start: Date, end: Date | null) => void;
}

const RangeCalendar: React.FC<RangeCalendarProps> = ({ startDate, endDate, onSelectRange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const days = getCalendarDays(year, month);

  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const isDateDisabled = (date: Date): boolean => {
    return isPastDate(date);
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === month;
  };

  const isInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isRangeStart = (date: Date): boolean => {
    return startDate ? isSameDay(date, startDate) : false;
  };

  const isRangeEnd = (date: Date): boolean => {
    return endDate ? isSameDay(date, endDate) : false;
  };

  const handleDateClick = (e: React.MouseEvent, date: Date) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDateDisabled(date)) return;

    if (selectingStart || !startDate) {
      // 시작 날짜 선택
      onSelectRange(date, null);
      setSelectingStart(false);
    } else {
      // 종료 날짜 선택
      if (date < startDate) {
        // 시작 날짜보다 이전이면 시작 날짜를 다시 선택
        onSelectRange(date, null);
      } else {
        // 정상적으로 종료 날짜 선택
        onSelectRange(startDate, date);
        setSelectingStart(true);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={(e) => handlePrevMonth(e)}
          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
          aria-label="이전 달"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h2 className="text-xs font-bold">
          {year}년 {month + 1}월
        </h2>

        <button
          onClick={(e) => handleNextMonth(e)}
          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
          aria-label="다음 달"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Selection Info */}
      <div className="mb-1 text-xs h-4">
        {!startDate ? (
          <p className="text-gray-600 text-[10px]">대여 시작 날짜 선택</p>
        ) : !endDate ? (
          <p className="text-gray-600 text-[10px]">반납 날짜 선택</p>
        ) : (
          <p className="text-primary-600 font-medium text-[10px]">
            {startDate.getMonth() + 1}월 {startDate.getDate()}일 ~ {endDate.getMonth() + 1}월 {endDate.getDate()}일
          </p>
        )}
      </div>

      {/* Week days */}
      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {weekDays.map((day, index) => (
          <div
            key={day}
            className={`text-center text-[10px] font-semibold py-0.5 ${
              index === 0 ? 'text-red-500' : index === 6 ? 'text-blue-500' : 'text-gray-700'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((date, index) => {
          const isSelected = isRangeStart(date) || isRangeEnd(date);
          const isDisabled = isDateDisabled(date);
          const isCurrent = isCurrentMonth(date);
          const isToday = isSameDay(date, new Date());
          const inRange = isInRange(date);
          const isStart = isRangeStart(date);
          const isEnd = isRangeEnd(date);

          return (
            <button
              key={index}
              onClick={(e) => handleDateClick(e, date)}
              disabled={isDisabled}
              className={`
                aspect-square p-1.5 text-xs font-semibold transition-all relative
                ${isCurrent ? 'text-gray-900' : 'text-gray-400'}
                ${isStart ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm rounded-l-2xl rounded-r-md' : ''}
                ${isEnd && !isStart ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm rounded-r-2xl rounded-l-md' : ''}
                ${inRange && !isSelected ? 'bg-blue-100 text-blue-900 hover:bg-blue-200 rounded' : ''}
                ${!isSelected && !isDisabled && isCurrent && !inRange ? 'bg-gray-50 hover:bg-blue-50 rounded' : ''}
                ${isDisabled ? 'cursor-not-allowed opacity-30 bg-gray-100 rounded' : 'cursor-pointer'}
                ${isToday && !isSelected && !inRange ? 'ring-1 ring-blue-400 bg-blue-50 rounded' : ''}
              `}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Reset Button */}
      {startDate && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelectRange(null as any, null);
            setSelectingStart(true);
          }}
          className="mt-1 w-full py-0.5 text-[10px] text-gray-600 hover:text-gray-900 transition-colors"
        >
          선택 초기화
        </button>
      )}
    </div>
  );
};

export default RangeCalendar;
